import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { storeRequestApi } from "../../../api/storeRequestApi";
import { onboardingApi } from "../../../api/onboardingApi";
import { paymentApi } from "../../../api/paymentApi";
import { messageApi } from "../../../api/messageApi";
import { useNotifications } from "../../../context/NotificationContext";
import { formatChatDateTime } from "../../../utils/chatDateTime";
import "./RequestStatus.css";

const EFT_DETAIL_LABELS = {
  bankName: "Bank",
  accountHolder: "Account holder",
  accountNumber: "Account number",
  branchCode: "Branch code",
  accountType: "Account type",
  paymentReference: "Payment reference",
};

const STATUS_MAP = {
  pending: { label: "Pending review", tone: "pending" },
  approved: { label: "Approved", tone: "approved" },
  rejected: { label: "Rejected", tone: "rejected" },
  in_progress: { label: "In progress", tone: "progress" },
  ready_for_review: { label: "Ready for review", tone: "review" },
  live: { label: "Live", tone: "live" },
  suspended: { label: "Suspended", tone: "suspended" },
};

const STAGES = [
  { title: "Request submitted", short: "Submitted" },
  { title: "Store setup", short: "Setup" },
  { title: "Team review", short: "Review" },
  { title: "Store live", short: "Live" },
];

const STAGE_INDEX = {
  pending: 0,
  approved: 1,
  in_progress: 1,
  ready_for_review: 2,
  live: 3,
  suspended: 3,
};

const STORE_REQUEST_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const parseAdditionalRequirements = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
      return JSON.parse(value);
  } catch {
      return {};
  }
};

const EFTDetailList = ({ details, instructions, fallback }) => {
  const configuredFields = Object.entries(EFT_DETAIL_LABELS).filter(([field]) =>
    String(details?.[field] || "").trim(),
  );

  if (configuredFields.length) {
    return (
      <dl className="bank-details-fields">
        {configuredFields.map(([field, label]) => (
          <div key={field}>
            <dt>{label}</dt>
            <dd>{details[field]}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <div className="bank-details-instructions">{instructions || fallback}</div>
  );
};

const DetailRow = ({ label, children }) => (
  <div className="request-detail-row">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const RequestStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { socket } = useNotifications();

  const [request, setRequest] = useState(null);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [paymentAttempt, setPaymentAttempt] = useState(null);
  const [paymentCheckout, setPaymentCheckout] = useState(null);
  const [startingPayment, setStartingPayment] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageDraft, setMessageDraft] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [communicationError, setCommunicationError] = useState("");
  const messageListRef = useRef(null);
  const paymentCheckoutFormRef = useRef(null);

  useEffect(() => {
    if (!paymentCheckout || !paymentCheckoutFormRef.current) return;
    paymentCheckoutFormRef.current.requestSubmit();
  }, [paymentCheckout]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (!event.persisted) return;
      setStartingPayment(false);
      setPaymentCheckout(null);
      setPaymentAttempt(null);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const fetchRequestDetails = useCallback(async (showLoading = true) => {
    if (!STORE_REQUEST_ID_PATTERN.test(String(id || ""))) {
      setRequest(null);
      setMessages([]);
      setError("A valid store request ID was not provided.");
      setLoading(false);
      return;
    }

    try {
      if (showLoading) setLoading(true);
      const [response, messagesResponse] = await Promise.all([
        storeRequestApi.getRequestById(id),
        messageApi.getMessages(id),
      ]);
      setRequest(response.data);
      setMessages(messagesResponse.data || []);

      const progressResponse = await onboardingApi.getProgress(id);
      const progData = progressResponse.data;
      if (progData) {
        const percentage =
          progData.TotalSteps > 0
            ? Math.round((progData.CompletedSteps / progData.TotalSteps) * 100)
            : 0;
        setProgress(percentage);
      }
    } catch (fetchError) {
      setError("Failed to load request details");
      console.error(fetchError);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [id]);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await paymentApi.getMethods();
      const candidates = response?.data?.data ?? response?.data ?? response;
      const methods = (Array.isArray(candidates) ? candidates : [])
        .map((method) => ({
          ...method,
          code: String(method.code ?? method.Code ?? "")
            .trim()
            .toLowerCase(),
          displayName: method.displayName ?? method.DisplayName ?? "",
          online: Boolean(method.online ?? method.IsOnline),
          instructions: String(
            method.instructions ?? method.Instructions ?? "",
          ).trim(),
        }))
        .filter((method) => method.code);
      setPaymentMethods(methods);
      setSelectedPaymentMethod((current) =>
        methods.some((method) => method.code === current)
          ? current
          : methods.find((method) => method.code === "eft")?.code ||
            methods[0]?.code ||
            "",
      );
    } catch (paymentError) {
      console.error("Failed to load payment methods:", paymentError);
    }
  }, []);

  useEffect(() => {
    fetchRequestDetails();
    fetchPaymentMethods();
  }, [fetchPaymentMethods, fetchRequestDetails]);

  useEffect(() => {
    if (!socket) return undefined;
    const refreshRequest = (event) => {
      if (event?.storeRequestId === id) fetchRequestDetails(false);
    };
    const refreshPlatformData = () => {
      fetchRequestDetails(false);
      fetchPaymentMethods();
    };
    socket.on("request:updated", refreshRequest);
    socket.on("platform:updated", refreshPlatformData);
    return () => {
      socket.off("request:updated", refreshRequest);
      socket.off("platform:updated", refreshPlatformData);
    };
  }, [fetchPaymentMethods, fetchRequestDetails, id, socket]);

  // Scroll only the message list (not the whole page) when messages change.
  useEffect(() => {
    const list = messageListRef.current;
    if (list) list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const selectedPaymentMethodDetails = paymentMethods.find(
    (method) => method.code === selectedPaymentMethod,
  );

  const sendClientMessage = async (event) => {
    event.preventDefault();
    const message = messageDraft.trim();
    if (
      !STORE_REQUEST_ID_PATTERN.test(String(id || "")) ||
      !message ||
      sendingMessage
    ) return;
    setSendingMessage(true);
    setCommunicationError("");
    try {
      await messageApi.sendMessage(id, message);
      setMessageDraft("");
      const response = await messageApi.getMessages(id);
      setMessages(response.data || []);
    } catch (sendError) {
      setCommunicationError(
        sendError.response?.data?.error || "Could not send your message.",
      );
    } finally {
      setSendingMessage(false);
    }
  };

  const handleMessageKeyDown = (event) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      sendClientMessage(event);
    }
  };

  const handleRetryPayment = async () => {
    if (
      !STORE_REQUEST_ID_PATTERN.test(String(id || "")) ||
      !selectedPaymentMethod
    ) return;
    try {
      setStartingPayment(true);
      setError("");
      setPaymentAttempt(null);
      setPaymentCheckout(null);
      const response = await paymentApi.startPayment(id, selectedPaymentMethod);
      setPaymentAttempt(response.data.payment);
      setPaymentCheckout(response.data.checkout);
      if (!response.data.checkout) {
        setStartingPayment(false);
        await fetchRequestDetails();
      }
    } catch (paymentError) {
      setError(
        paymentError.response?.data?.error || "Unable to start payment.",
      );
      setStartingPayment(false);
    }
  };

  const getActions = () => {
    if (!request) return null;
    const actions = {
      approved: (
        <button
          className="btn-primary"
          onClick={() => navigate("/subscription")}
        >
          Review setup details
        </button>
      ),
      in_progress: (
        <button
          className="btn-primary"
          onClick={() => navigate("/subscription")}
        >
          Review setup details
        </button>
      ),
      live: (
        <button
          className="btn-success"
          onClick={() => window.open(request.StoreUrl, "_blank", "noopener")}
        >
          View store
        </button>
      ),
      rejected: (
        <button className="btn-secondary" onClick={() => navigate("/support")}>
          Contact support
        </button>
      ),
      suspended: (
        <button className="btn-secondary" onClick={() => navigate("/support")}>
          Contact support
        </button>
      ),
    };
    return actions[request.Status] || null;
  };

  if (loading) {
    return (
      <div className="loading-screen" role="status">
        <div className="spinner"></div>
        <p>Loading request details...</p>
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="dashboard-card request-status-page request-empty-state">
        <h3>Request not found</h3>
        <p>{error || "The store request could not be found."}</p>
        <button className="btn-primary" onClick={() => navigate("/dashboard")}>
          Go to dashboard
        </button>
      </div>
    );
  }

  if (!request) return null;

  const statusInfo = STATUS_MAP[request.Status] || {
    label: request.Status,
    tone: "pending",
  };
  const stageIndex = STAGE_INDEX[request.Status] ?? 0;
  const stageProgress =
    request.Status === "in_progress"
      ? 34 + Math.round(progress * 0.2)
      : [0, 34, 78, 100][stageIndex];

  const nextSteps = {
    pending: {
      summary: "Estimated 24–48 hours",
      items: [
        "Our team is reviewing your request",
        "We'll email you once it's approved",
      ],
    },
    approved: {
      summary: "Select “Review setup details” to begin",
      items: ["Your request has been approved", "Start your onboarding"],
    },
    in_progress: {
      summary: `${progress}% complete`,
      items: [
        "Complete all 10 onboarding steps",
        "Submit for review when you're done",
      ],
    },
    ready_for_review: {
      summary: "Estimated 12–24 hours",
      items: [
        "Our team is reviewing your store",
        "We'll notify you when it's done",
      ],
    },
    live: {
      summary: request.StoreUrl || "Store URL available soon",
      items: ["Your store is live", "Visit it and start selling"],
    },
    rejected: {
      summary: "We're here to help",
      items: [
        "This request was not approved",
        "Contact support to talk it through",
      ],
    },
    suspended: {
      summary: "Support can help restore access",
      items: [
        "Your store is currently suspended",
        "Contact support for assistance",
      ],
    },
  }[request.Status];

  const requestRequirements = parseAdditionalRequirements(
    request.AdditionalRequirements,
  );
  const invoiceTotalDue = Number(requestRequirements.invoice?.totalDue);
  const hasInvoiceAmount =
    Number.isFinite(invoiceTotalDue) && invoiceTotalDue > 0;
  const showPaymentPanel =
    !request.IsTrial &&
    (request.LatestPaymentID || hasInvoiceAmount) &&
    !["rejected", "suspended", "live"].includes(
      String(request.Status || "").toLowerCase(),
    );
  const paymentDone = request.PaymentStatus === "completed";
  const lockedStatuses = [
    "approved",
    "in_progress",
    "ready_for_review",
    "live",
    "suspended",
  ];
  const canEditRequest =
    Boolean(request.ClientEditingUnlocked) ||
    (!request.ApprovedDate && !lockedStatuses.includes(request.Status));

  return (
    <div className="request-status-page">
      {startingPayment && (
        <div className="payment-loading-overlay" role="status" aria-live="polite">
          <span className="payment-loading-spinner" aria-hidden="true" />
          <p>
            {paymentCheckout
              ? `Redirecting to ${paymentAttempt?.method || "the payment gateway"}...`
              : "Preparing your payment..."}
          </p>
        </div>
      )}
      <header className="request-status-header">
        <div>
          <h2>Store request status</h2>
          <p>Request #{request.RequestNumber}</p>
        </div>
        <span className={`request-badge request-badge--${statusInfo.tone}`}>
          <span className="request-badge-dot" aria-hidden="true" />
          {statusInfo.label}
        </span>
      </header>

      {error && (
        <div className="request-banner request-banner--error" role="alert">
          {error}
        </div>
      )}

      <div className="request-status-grid">
        <div className="dashboard-card request-status-summary-card">
          {request.Status !== "rejected" ? (
            <section
              className={`request-stage-progress ${
                request.Status === "suspended" ? "is-suspended" : ""
              }`}
              aria-label="Store request progress"
            >
              <div className="request-stage-track" aria-hidden="true">
                <div
                  className="request-stage-fill"
                  style={{ width: `${stageProgress}%` }}
                />
              </div>
              <ol className="request-stage-list">
                {STAGES.map(({ title, short }, index) => (
                  <li
                    key={title}
                    className={
                      index < stageIndex
                        ? "is-complete"
                        : index === stageIndex
                          ? "is-current"
                          : ""
                    }
                    aria-current={index === stageIndex ? "step" : undefined}
                  >
                    <span className="request-stage-marker">
                      {index < stageIndex ? "✓" : index + 1}
                    </span>
                    <span className="request-stage-label">{title}</span>
                    <span className="request-stage-mobile-label">{short}</span>
                  </li>
                ))}
              </ol>
              {request.Status === "in_progress" && (
                <p className="request-status-notice">
                  Onboarding details completed: <strong>{progress}%</strong>
                </p>
              )}
              {request.Status === "suspended" && (
                <p className="request-status-notice request-status-notice--warning">
                  Your store is currently suspended. Please contact support for
                  assistance.
                </p>
              )}
            </section>
          ) : (
            <div className="request-status-notice request-status-notice--error">
              This request was not approved. Contact support if you need help.
            </div>
          )}

          <h3 className="request-section-title">Request details</h3>
          <dl className="request-status-details">
            <DetailRow label="Business">{request.BusinessName}</DetailRow>
            <DetailRow label="Package">
              {request.PackageName} ({request.Tier})
            </DetailRow>
            {request.IsTrial && (
              <DetailRow label="Trial">
                Active until{" "}
                {request.TrialEndsAt
                  ? formatDate(request.TrialEndsAt)
                  : "date not available"}
                <span className="request-detail-hint">
                  Toora Flex subdomain only
                </span>
              </DetailRow>
            )}
            <DetailRow label="Submitted">
              {formatDate(request.SubmittedDate)}
            </DetailRow>
            {request.ApprovedDate && (
              <DetailRow label="Approved">
                {formatDate(request.ApprovedDate)}
              </DetailRow>
            )}
            {request.LaunchedDate && (
              <DetailRow label="Launched">
                {formatDate(request.LaunchedDate)}
              </DetailRow>
            )}
          </dl>

          {request.RejectionReason && (
            <div className="request-banner request-banner--error">
              <strong>Reason:</strong> {request.RejectionReason}
            </div>
          )}

          {showPaymentPanel && (
            <section
              className={`request-payment ${paymentDone ? "is-complete" : ""}`}
              aria-label="Payment"
            >
              <div className="request-payment-header">
                <h3>Payment</h3>
                <span
                  className={`request-pill ${
                    paymentDone ? "request-pill--success" : "request-pill--info"
                  }`}
                >
                  {paymentDone
                    ? "Completed"
                    : request.PaymentStatus ||
                      (request.LatestPaymentID ? "Pending" : "Not paid")}
                </span>
              </div>

              <p className="request-payment-amount">
                {request.LatestPaymentCurrency || "ZAR"}{" "}
                {Number(
                  request.LatestPaymentAmount || invoiceTotalDue || 0,
                ).toFixed(2)}
                {request.LatestPaymentMethod && (
                  <span> via {request.LatestPaymentMethod}</span>
                )}
              </p>
              {request.LatestPaymentReference && (
                <p className="request-payment-meta">
                  Reference: <strong>{request.LatestPaymentReference}</strong>
                </p>
              )}
              {request.LatestPaymentInstructions && (
                <p className="request-payment-instructions">
                  {request.LatestPaymentInstructions}
                </p>
              )}
              {request.PaymentVerifiedAt && (
                <p className="request-payment-meta">
                  Confirmed {formatDate(request.PaymentVerifiedAt)}
                </p>
              )}
              {!paymentDone && request.LatestPaymentID && (
                <p className="request-payment-meta">
                  Store setup can begin after your payment is received and
                  verified. Its status will be reflected here.
                </p>
              )}
              {!paymentDone && !request.LatestPaymentID && (
                <p className="request-payment-meta">
                  Payment is required before store setup can begin. Pay now
                  below, or return to this page when you are ready to pay.
                </p>
              )}

              {!paymentDone && paymentMethods.length > 0 && (
                <div className="request-payment-actions">
                  <label htmlFor="retry-payment-method">
                    Choose a payment option
                  </label>
                  <div className="request-payment-controls">
                    <select
                      id="retry-payment-method"
                      value={selectedPaymentMethod}
                      onChange={(event) => {
                        setSelectedPaymentMethod(event.target.value);
                        setPaymentAttempt(null);
                        setPaymentCheckout(null);
                      }}
                    >
                      {paymentMethods.map((method) => (
                        <option key={method.code} value={method.code}>
                          {method.displayName}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleRetryPayment}
                      disabled={!selectedPaymentMethod || startingPayment}
                    >
                      {startingPayment ? (
                        <>
                          <span className="spinner-small" aria-hidden="true" />
                          {paymentCheckout ? "Redirecting..." : "Preparing..."}
                        </>
                      ) : (
                        "Pay now"
                      )}
                    </button>
                  </div>
                  {selectedPaymentMethodDetails?.paymentImage && (
                    <div className="request-payment-method-image">
                      <img
                        src={selectedPaymentMethodDetails.paymentImage}
                        alt={`${selectedPaymentMethodDetails.displayName} payment`}
                      />
                    </div>
                  )}
                  {!paymentDone && paymentMethods.length === 0 && (
                    <p className="request-banner request-banner--error" role="alert">
                      Payment methods are currently unavailable. Please contact
                      support to arrange payment.
                    </p>
                  )}

                  {selectedPaymentMethodDetails &&
                    (selectedPaymentMethodDetails.code === "eft" ||
                      !selectedPaymentMethodDetails.online) && (
                      <div className="bank-details-card">
                        <div className="bank-details-heading">
                          <span
                            className="bank-details-icon"
                            aria-hidden="true"
                          >
                            <i className="fas fa-university"></i>
                          </span>
                          <div>
                            <h5>Bank transfer details</h5>
                            <p>
                              {selectedPaymentMethodDetails.instructions
                                ? "Use these details to make your transfer."
                                : "Bank details have not been configured yet."}
                            </p>
                          </div>
                        </div>
                        <EFTDetailList
                          details={selectedPaymentMethodDetails.eftDetails}
                          instructions={
                            selectedPaymentMethodDetails.instructions
                          }
                          fallback="The administrator needs to add the bank details in Admin > Payments before an EFT transfer can be made."
                        />
                      </div>
                    )}

                  {paymentAttempt && (
                    <div className="request-payment-attempt">
                      <strong>
                        {paymentAttempt.currency}{" "}
                        {Number(paymentAttempt.amount).toFixed(2)} ·{" "}
                        {paymentAttempt.reference}
                      </strong>
                      {paymentCheckout && (
                        <form
                          ref={paymentCheckoutFormRef}
                          method="post"
                          action={paymentCheckout.action}
                          hidden
                          aria-hidden="true"
                        >
                          {Object.entries(paymentCheckout.fields).map(
                            ([name, value]) => (
                              <input
                                key={name}
                                type="hidden"
                                name={name}
                                value={value}
                              />
                            ),
                          )}
                        </form>
                      )}
                      {startingPayment && paymentCheckout && (
                        <p className="payment-gateway-redirect" role="status">
                          <span className="spinner-small" aria-hidden="true" />
                          Redirecting to {paymentAttempt.method}...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {(getActions() || canEditRequest) && (
            <div className="request-status-actions">
              {canEditRequest && (
                <button
                  className="btn-primary"
                  onClick={() =>
                    navigate(`/request-store?requestId=${encodeURIComponent(id)}`)
                  }
                >
                  Edit request
                </button>
              )}
              {getActions()}
            </div>
          )}
        </div>

        <aside className="dashboard-card request-next-steps-card">
          <h3>Next steps</h3>
          <ul className="request-next-steps-list">
            {nextSteps?.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {nextSteps?.summary && (
            <p className="request-next-steps-summary">{nextSteps.summary}</p>
          )}
        </aside>
      </div>

      <section className="dashboard-card request-communication-card">
        <div className="request-communication-heading">
          <div>
            <h3>Conversation with Toora Flex</h3>
            <p>
              Message the team about this request. Replies appear here live.
            </p>
          </div>
          <span className="request-communication-presence">
            <span aria-hidden="true" /> Request #{request.RequestNumber}
          </span>
        </div>

        {communicationError && (
          <div className="request-banner request-banner--error" role="alert">
            {communicationError}
          </div>
        )}

        <div
          className="request-message-list"
          ref={messageListRef}
          aria-live="polite"
        >
          {messages.length ? (
            messages.map((message) => {
              const isClientMessage =
                String(message.SenderType).toLowerCase() === "client";
              return (
                <article
                  className={`request-message ${
                    isClientMessage ? "is-client" : "is-team"
                  }`}
                  key={message.MessageID}
                >
                  <div className="request-message-meta">
                    <strong>
                      {isClientMessage ? "You" : "Toora Flex team"}
                    </strong>
                    <time dateTime={message.CreatedAt}>
                      {formatChatDateTime(message.CreatedAt)}
                    </time>
                  </div>
                  <p>{message.Message}</p>
                </article>
              );
            })
          ) : (
            <p className="request-message-empty">
              No messages yet. Send one if you need help with this request.
            </p>
          )}
        </div>

        <form className="request-message-form" onSubmit={sendClientMessage}>
          <label className="sr-only" htmlFor="request-message-input">
            Write a message to Toora Flex
          </label>
          <textarea
            id="request-message-input"
            value={messageDraft}
            onChange={(event) => setMessageDraft(event.target.value)}
            onKeyDown={handleMessageKeyDown}
            placeholder="Write a message..."
            maxLength={5000}
            rows={2}
            required
          />
          <button
            type="submit"
            className="btn-primary"
            disabled={!messageDraft.trim() || sendingMessage}
          >
            {sendingMessage ? "Sending..." : "Send message"}
          </button>
        </form>
        <p className="request-message-hint">Press Ctrl + Enter to send.</p>
      </section>
    </div>
  );
};

export default RequestStatus;
